import { InvoiceTableDataRow, InvoiceTableTabs, useInvoicesTableProps, InvoicesResponse } from './types.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { filterDocumentsBasedOnTab, memoizedFilterOutDirectDebitBounce } from './utils.tsx'
import { useGetCustomerAccountingQuery } from 'store/queries/bolt-backend/customers'
import { getInvoiceTableData, getSuperUserInvoiceTableData, getTransactionTableData } from './tableData.tsx'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { SMALL_DESKTOP_BREAKPOINT } from 'constants/viewport.ts'
import { selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay } from 'store/contact/slice.ts'
import { ACCOUNTING_ENTITIES, CustomerAccountingDocument, CustomerInvoice, CustomerPayment } from '@boltenergy-be/shared'
import { Capacitor } from '@capacitor/core'
import { UserTypes } from 'store/auth/types.ts'

/**
 * Returns the table 'headers' and 'rows' for a given route.
 * @param {useInvoicesTableProps} props
 */
const useInvoicesTable = ({ tab, selectedDocumentId }: useInvoicesTableProps): InvoicesResponse => {
  // Redux state
  const { selected, accounts } = useStoreSelector((store) => store.contact)
  const { userType } = useStoreSelector((store) => store.auth)
  const isSuperUser = userType === UserTypes.SUPER_USER
  const hasContractOlderThanOneDay = useStoreSelector(selectCurrentAccountHasContractWithSubscriptionDateOlderThanOneDay)
  const language = useGetLanguage()

  // Redux queries
  const isNativeApp = Capacitor.isNativePlatform()
  const { data, isLoading } = useGetCustomerAccountingQuery(
    { customerNumber: selected.account },
    {
      skip: !hasContractOlderThanOneDay,
      refetchOnFocus: isNativeApp,
      refetchOnMountOrArgChange: true
    }
  )

  // constants
  const hasMultipleContracts = selected.account in accounts ? accounts[selected.account].billingContracts.length > 1 : false
  const filteredDocumentsByTypeBasedOnRoute = filterDocumentsBasedOnTab({ tab, documents: data?.documents ?? [] })
  const usesDirectDebit = !!accounts[selected.account]?.paymentDetails?.directDebit
  const filteredDocuments = memoizedFilterOutDirectDebitBounce({
    documents: filteredDocumentsByTypeBasedOnRoute,
    tab
  })
  const isAlsoSupplier = isSuperUser ? !!data?.documents?.some((doc) => doc.accountingEntity === ACCOUNTING_ENTITIES.supplier) : false
  const returnProps: Pick<InvoicesResponse, 'isLoading' | 'isAlsoSupplier'> = { isLoading, isAlsoSupplier }

  // Window size
  const { isSmaller: isTabletOrMobile } = useWindowSize(SMALL_DESKTOP_BREAKPOINT)

  /**
   * Builds the table rows for the active tab
   * @param {CustomerAccountingDocument[]} documents
   * @returns {InvoiceTableDataRow[]}
   */
  const getRows = (documents: CustomerAccountingDocument[]): InvoiceTableDataRow[] => {
    switch (tab) {
      case InvoiceTableTabs.SUPER_USER_CUSTOMER:
      case InvoiceTableTabs.SUPER_USER_SUPPLIER:
        return getSuperUserInvoiceTableData({
          documents,
          isTabletOrMobile,
          language,
          showAddress: hasMultipleContracts,
          usesDirectDebit
        })

      case InvoiceTableTabs.OUTSTANDING_INVOICES:
      case InvoiceTableTabs.ALL_INVOICES:
        return getInvoiceTableData({
          documents: documents as CustomerInvoice[],
          isTabletOrMobile,
          language,
          showAddress: hasMultipleContracts,
          usesDirectDebit
        })

      case InvoiceTableTabs.ALL_TRANSACTIONS:
        return getTransactionTableData({
          documents: documents as CustomerPayment[],
          isTabletOrMobile,
          language,
          showAddress: false,
          usesDirectDebit
        })

      default:
        return []
    }
  }

  // Looked up in the unfiltered documents so the selected row survives leaving the active tab (e.g. once paid)
  const selectedDocument = data?.documents.find((document) => document.id === selectedDocumentId)

  return {
    ...returnProps,
    data: getRows(filteredDocuments),
    selectedRow: selectedDocument ? getRows([selectedDocument])[0] : undefined
  }
}

export default useInvoicesTable
